import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Form, Button, Spinner } from 'react-bootstrap';
import {
  FaUser,
  FaEnvelope,
  FaMapMarkerAlt,
  FaUserTag,
  FaCamera,
  FaLock,
  FaKey,
  FaSave,
  FaShieldAlt,
  FaCalendarAlt,
  FaClock,
  FaCheckCircle,
  FaUserCircle,
  FaBell
} from 'react-icons/fa';
import Swal from 'sweetalert2';
import '../../../../assets/css/admin/dashboard/AdminProfile.css';
import AdminLayout from '../../../../layouts/admin/AdminLayout';

const AP_BASE_URL = 'http://localhost:8000/api/admin';

const AdminProfile = () => {
  // Logged in admin ki information (Login.js se)
  const adminData = JSON.parse(localStorage.getItem("adminData"));

  const AP_adminId = adminData?.id;

  // Profile Data State
  const [profileData, setProfileData] = useState({
    name: '',
    email: '',
    address: '',
    profilePicture: null
  });

  // Selected file (upload ke liye) preview se separate
  const [AP_selectedImage, setAP_selectedImage] = useState(null);

  // Password State
  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });

  // Account Information
  const [accountInfo, setAccountInfo] = useState({
    role: '',
    createdOn: '',
    lastLogin: '',
    status: ''
  });

  // Notification Settings State
  const [notificationSettings, setNotificationSettings] = useState({
    emailNotifications: true,
    inAppNotifications: true
  });

  const [AP_loading, setAP_loading] = useState(true);
  const [AP_saving, setAP_saving] = useState(false);
  const [AP_changingPassword, setAP_changingPassword] = useState(false);
  const [AP_savingNotifications, setAP_savingNotifications] = useState(false);

  // ===================== VIEW PROFILE (READ) =====================
  const AP_fetchProfile = () => {
    setAP_loading(true);

    const formData = new FormData();
    formData.append('id', AP_adminId);

    fetch(`${AP_BASE_URL}/view-profile`, {
      method: 'POST',
      body: formData
    })
      .then((res) => res.json())
      .then((response) => {
        if (response.success) {
          const admin = response.admin;
          setProfileData({
            name: admin.name || '',
            email: admin.email || '',
            address: admin.address || '',
            profilePicture: admin.image ? `http://localhost:8000/storage/${admin.image}` : null
          });
          setAccountInfo({
            role: admin.role || 'System Administrator',
            createdOn: admin.created_at ? admin.created_at.substring(0, 10) : '',
            lastLogin: admin.last_login || '',
            status: admin.status || 'Active'
          });
        } else {
          Swal.fire({
            icon: 'error',
            title: 'Error!',
            text: response.message || 'Unable to load profile',
            confirmButtonColor: '#7fd3c1'
          });
        }
      })
      .catch((error) => {
        console.error(error);
        Swal.fire({
          icon: 'error',
          title: 'Error!',
          text: 'Something went wrong while loading your profile',
          confirmButtonColor: '#7fd3c1'
        });
      })
      .finally(() => {
        setAP_loading(false);
      });
  };

  // ===================== VIEW NOTIFICATION SETTINGS (READ) =====================
  const AP_fetchNotificationSettings = () => {
    const formData = new FormData();
    formData.append('id', AP_adminId);

    fetch(`${AP_BASE_URL}/view-notification-settings`, {
      method: 'POST',
      body: formData
    })
      .then((res) => res.json())
      .then((response) => {
        if (response.success) {
          const settings = response.settings;
          setNotificationSettings({
            emailNotifications: !!settings.emailNotifications,
            inAppNotifications: !!settings.inAppNotifications
          });
        } else {
          Swal.fire({
            icon: 'error',
            title: 'Error!',
            text: response.message || 'Unable to load notification settings',
            confirmButtonColor: '#7fd3c1'
          });
        }
      })
      .catch((error) => {
        console.error(error);
        Swal.fire({
          icon: 'error',
          title: 'Error!',
          text: 'Something went wrong while loading notification settings',
          confirmButtonColor: '#7fd3c1'
        });
      });
  };

  useEffect(() => {

    if (!AP_adminId) {

        Swal.fire({
            icon: "error",
            title: "Session Expired",
            text: "Please login again."
        });

        return;
    }

    AP_fetchProfile();
    AP_fetchNotificationSettings();

}, [AP_adminId]);

  // ===================== HANDLE PROFILE PICTURE SELECT =====================
  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 5000000) {
        // 5MB limit
        Swal.fire({
          icon: 'error',
          title: 'File Too Large!',
          text: 'Please upload an image smaller than 5MB',
          confirmButtonColor: '#7fd3c1'
        });
        return;
      }

      setAP_selectedImage(file);

      const reader = new FileReader();
      reader.onloadend = () => {
        setProfileData({ ...profileData, profilePicture: reader.result });
      };
      reader.readAsDataURL(file);
    }
  };

  // ===================== UPDATE PROFILE =====================
  const handleProfileUpdate = (e) => {
    e.preventDefault();

    // Validation
    if (!profileData.name || !profileData.email) {
      Swal.fire({
        icon: 'error',
        title: 'Validation Error!',
        text: 'Please fill all required fields',
        confirmButtonColor: '#7fd3c1'
      });
      return;
    }

    // Email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(profileData.email)) {
      Swal.fire({
        icon: 'error',
        title: 'Invalid Email!',
        text: 'Please enter a valid email address',
        confirmButtonColor: '#7fd3c1'
      });
      return;
    }

    setAP_saving(true);

    const formData = new FormData();
    formData.append('id', AP_adminId);
    formData.append('name', profileData.name);
    formData.append('email', profileData.email);
    formData.append('address', profileData.address);
    if (AP_selectedImage) {
      formData.append('file', AP_selectedImage);
    }

    fetch(`${AP_BASE_URL}/update-profile`, {
      method: 'POST',
      body: formData
    })
      .then((res) => res.json())
      .then((response) => {
        if (response.success) {
          Swal.fire({
            icon: 'success',
            title: 'Profile Updated!',
            text: 'Your profile has been updated successfully',
            confirmButtonColor: '#7fd3c1',
            timer: 2000,
            showConfirmButton: false
          });
          AP_fetchProfile();
          setAP_selectedImage(null);
        } else {
          Swal.fire({
            icon: 'error',
            title: 'Error!',
            text: response.message || 'Unable to update profile',
            confirmButtonColor: '#7fd3c1'
          });
        }
      })
      .catch((error) => {
        console.error(error);
        Swal.fire({
          icon: 'error',
          title: 'Error!',
          text: 'Something went wrong while updating your profile',
          confirmButtonColor: '#7fd3c1'
        });
      })
      .finally(() => {
        setAP_saving(false);
      });
  };

  // ===================== UPDATE PASSWORD =====================
  const handlePasswordUpdate = (e) => {
    e.preventDefault();

    // Validation
    if (!passwordData.currentPassword || !passwordData.newPassword || !passwordData.confirmPassword) {
      Swal.fire({
        icon: 'error',
        title: 'Validation Error!',
        text: 'Please fill all password fields',
        confirmButtonColor: '#7fd3c1'
      });
      return;
    }

    // Password length check
    if (passwordData.newPassword.length < 8) {
      Swal.fire({
        icon: 'error',
        title: 'Weak Password!',
        text: 'Password must be at least 8 characters long',
        confirmButtonColor: '#7fd3c1'
      });
      return;
    }

    // Password match check
    if (passwordData.newPassword !== passwordData.confirmPassword) {
      Swal.fire({
        icon: 'error',
        title: 'Passwords Do Not Match!',
        text: 'New password and confirm password must be the same',
        confirmButtonColor: '#7fd3c1'
      });
      return;
    }

    setAP_changingPassword(true);

    const formData = new FormData();
    formData.append('id', AP_adminId);
    formData.append('currentPassword', passwordData.currentPassword);
    formData.append('newPassword', passwordData.newPassword);

    fetch(`${AP_BASE_URL}/change-password`, {
      method: 'POST',
      body: formData
    })
      .then((res) => res.json())
      .then((response) => {
        if (response.success) {
          Swal.fire({
            icon: 'success',
            title: 'Password Updated!',
            text: 'Your password has been changed successfully',
            confirmButtonColor: '#7fd3c1',
            timer: 2000,
            showConfirmButton: false
          });
          setPasswordData({
            currentPassword: '',
            newPassword: '',
            confirmPassword: ''
          });
        } else {
          Swal.fire({
            icon: 'error',
            title: 'Error!',
            text: response.message || 'Current password is incorrect',
            confirmButtonColor: '#7fd3c1'
          });
        }
      })
      .catch((error) => {
        console.error(error);
        Swal.fire({
          icon: 'error',
          title: 'Error!',
          text: 'Something went wrong while updating your password',
          confirmButtonColor: '#7fd3c1'
        });
      })
      .finally(() => {
        setAP_changingPassword(false);
      });
  };

  // ===================== UPDATE NOTIFICATION SETTINGS =====================
  const handleNotificationToggle = (field) => {
    const updatedSettings = {
      ...notificationSettings,
      [field]: !notificationSettings[field]
    };

    setNotificationSettings(updatedSettings);
    setAP_savingNotifications(true);

    const formData = new FormData();
    formData.append('id', AP_adminId);
    formData.append('emailNotifications', updatedSettings.emailNotifications ? 1 : 0);
    formData.append('inAppNotifications', updatedSettings.inAppNotifications ? 1 : 0);

    fetch(`${AP_BASE_URL}/update-notification-settings`, {
      method: 'POST',
      body: formData
    })
      .then((res) => res.json())
      .then((response) => {
        if (!response.success) {
          Swal.fire({
            icon: 'error',
            title: 'Error!',
            text: response.message || 'Unable to update notification settings',
            confirmButtonColor: '#7fd3c1'
          });
          // Revert toggle agar backend fail ho jaye
          AP_fetchNotificationSettings();
        }
      })
      .catch((error) => {
        console.error(error);
        Swal.fire({
          icon: 'error',
          title: 'Error!',
          text: 'Something went wrong while updating notification settings',
          confirmButtonColor: '#7fd3c1'
        });
        AP_fetchNotificationSettings();
      })
      .finally(() => {
        setAP_savingNotifications(false);
      });
  };

  // Handle Input Change
  const handleInputChange = (field, value) => {
    setProfileData({ ...profileData, [field]: value });
  };

  const handlePasswordChange = (field, value) => {
    setPasswordData({ ...passwordData, [field]: value });
  };

  if (AP_loading) {
    return (
      <AdminLayout>
        <div className="prf-profile-page d-flex justify-content-center align-items-center" style={{ minHeight: '60vh' }}>
          <Spinner animation="border" />
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
    <div className="prf-profile-page">
      <Container fluid className="prf-container">
        {/* Page Header */}
        <div className="prf-page-header">
          <div className="prf-header-content">
            <h1 className="prf-page-title">
              <FaUser className="prf-title-icon" />
              Admin Profile
            </h1>
            <p className="prf-page-subtitle">Manage your personal information and account settings</p>
          </div>
        </div>

        <Row className="prf-content-row">
          {/* Left Column - Profile Card */}
          <Col lg={4}>
            <Card className="prf-profile-card">
              <Card.Body className="prf-profile-body">
                {/* Profile Picture */}
                <div className="prf-picture-section">
                  <div className="prf-picture-wrapper">
                    {profileData.profilePicture ? (
                      <img
                        src={profileData.profilePicture}
                        alt="Profile"
                        className="prf-picture-img"
                      />
                    ) : (
                      <div className="prf-picture-placeholder">
                        <FaUserCircle className="prf-placeholder-icon" />
                      </div>
                    )}
                    <label htmlFor="profile-upload" className="prf-upload-overlay">
                      <FaCamera className="prf-camera-icon" />
                      <span>Change Photo</span>
                    </label>
                    <input
                      type="file"
                      id="profile-upload"
                      className="prf-file-input"
                      accept="image/*"
                      onChange={handleImageUpload}
                    />
                  </div>
                </div>

                {/* Profile Info */}
                <div className="prf-info-section">
                  <h3 className="prf-name">{profileData.name}</h3>
                  <p className="prf-role">{accountInfo.role}</p>
                  <p className="prf-email">
                    <FaEnvelope className="prf-info-icon" />
                    {profileData.email}
                  </p>
                </div>

                {/* Account Status */}
                <div className="prf-status-badge">
                  <FaCheckCircle className="prf-status-icon" />
                  <span>Account {accountInfo.status}</span>
                </div>
              </Card.Body>
            </Card>

            {/* Account Information Card */}
            <Card className="prf-section-card prf-account-card">
              <Card.Header className="prf-card-header">
                <h3 className="prf-section-title">
                  <FaShieldAlt className="prf-section-icon" />
                  Account Information
                </h3>
              </Card.Header>
              <Card.Body className="prf-card-body">
                <div className="prf-account-info">
                  <div className="prf-info-item">
                    <span className="prf-info-label">
                      <FaUserTag className="prf-label-icon" />
                      Role
                    </span>
                    <span className="prf-info-value">{accountInfo.role}</span>
                  </div>
                  <div className="prf-info-item">
                    <span className="prf-info-label">
                      <FaCalendarAlt className="prf-label-icon" />
                      Created On
                    </span>
                    <span className="prf-info-value">{accountInfo.createdOn}</span>
                  </div>
                  <div className="prf-info-item">
                    <span className="prf-info-label">
                      <FaClock className="prf-label-icon" />
                      Last Login
                    </span>
                    <span className="prf-info-value">{accountInfo.lastLogin}</span>
                  </div>
                </div>
              </Card.Body>
            </Card>

            {/* Notification Settings Card */}
            <Card className="prf-section-card prf-notification-card">
              <Card.Header className="prf-card-header">
                <h3 className="prf-section-title">
                  <FaBell className="prf-section-icon" />
                  Notification Settings
                </h3>
              </Card.Header>
              <Card.Body className="prf-card-body">
                <div className="prf-notification-list">
                  <div className="prf-notification-item">
                    <div className="prf-notification-text">
                      <span className="prf-notification-title">Email Notifications</span>
                      <span className="prf-notification-desc">Receive updates via email</span>
                    </div>
                    <Form.Check
                      type="switch"
                      id="notif-email-notifications"
                      className="prf-notification-switch"
                      checked={notificationSettings.emailNotifications}
                      onChange={() => handleNotificationToggle('emailNotifications')}
                      disabled={AP_savingNotifications}
                    />
                  </div>

                  <div className="prf-notification-item">
                    <div className="prf-notification-text">
                      <span className="prf-notification-title">In-App Notifications</span>
                      <span className="prf-notification-desc">Show alerts inside the admin panel</span>
                    </div>
                    <Form.Check
                      type="switch"
                      id="notif-in-app-notifications"
                      className="prf-notification-switch"
                      checked={notificationSettings.inAppNotifications}
                      onChange={() => handleNotificationToggle('inAppNotifications')}
                      disabled={AP_savingNotifications}
                    />
                  </div>
                </div>
              </Card.Body>
            </Card>
          </Col>

          {/* Right Column - Forms */}
          <Col lg={8}>
            {/* Personal Information */}
            <Card className="prf-section-card">
              <Card.Header className="prf-card-header">
                <h3 className="prf-section-title">
                  <FaUser className="prf-section-icon" />
                  Personal Information
                </h3>
              </Card.Header>
              <Card.Body className="prf-card-body">
                <Form onSubmit={handleProfileUpdate}>
                  <Row>
                    <Col md={6}>
                      <Form.Group className="prf-form-group">
                        <Form.Label className="prf-form-label">
                          <FaUser className="prf-label-icon" />
                          Name *
                        </Form.Label>
                        <Form.Control
                          type="text"
                          className="prf-form-input"
                          value={profileData.name}
                          onChange={(e) => handleInputChange('name', e.target.value)}
                          placeholder="Enter your name"
                          required
                        />
                      </Form.Group>
                    </Col>
                    <Col md={6}>
                      <Form.Group className="prf-form-group">
                        <Form.Label className="prf-form-label">
                          <FaEnvelope className="prf-label-icon" />
                          Email *
                        </Form.Label>
                        <Form.Control
                          type="email"
                          className="prf-form-input"
                          value={profileData.email}
                          onChange={(e) => handleInputChange('email', e.target.value)}
                          placeholder="Enter your email"
                          required
                        />
                      </Form.Group>
                    </Col>
                    <Col md={12}>
                      <Form.Group className="prf-form-group">
                        <Form.Label className="prf-form-label">
                          <FaMapMarkerAlt className="prf-label-icon" />
                          Address
                        </Form.Label>
                        <Form.Control
                          as="textarea"
                          rows={3}
                          className="prf-form-textarea"
                          value={profileData.address}
                          onChange={(e) => handleInputChange('address', e.target.value)}
                          placeholder="Enter your address"
                        />
                      </Form.Group>
                    </Col>
                    <Col md={12}>
                      <Button type="submit" className="prf-btn prf-btn-primary" disabled={AP_saving}>
                        <FaSave className="prf-btn-icon" />
                        {AP_saving ? 'Saving...' : 'Save Changes'}
                      </Button>
                    </Col>
                  </Row>
                </Form>
              </Card.Body>
            </Card>

            {/* Change Password */}
            <Card className="prf-section-card">
              <Card.Header className="prf-card-header">
                <h3 className="prf-section-title">
                  <FaLock className="prf-section-icon" />
                  Change Password
                </h3>
              </Card.Header>
              <Card.Body className="prf-card-body">
                <Form onSubmit={handlePasswordUpdate}>
                  <Row>
                    <Col md={12}>
                      <Form.Group className="prf-form-group">
                        <Form.Label className="prf-form-label">
                          <FaLock className="prf-label-icon" />
                          Current Password *
                        </Form.Label>
                        <Form.Control
                          type="password"
                          className="prf-form-input"
                          value={passwordData.currentPassword}
                          onChange={(e) => handlePasswordChange('currentPassword', e.target.value)}
                          placeholder="Enter current password"
                          required
                        />
                      </Form.Group>
                    </Col>
                    <Col md={6}>
                      <Form.Group className="prf-form-group">
                        <Form.Label className="prf-form-label">
                          <FaKey className="prf-label-icon" />
                          New Password *
                        </Form.Label>
                        <Form.Control
                          type="password"
                          className="prf-form-input"
                          value={passwordData.newPassword}
                          onChange={(e) => handlePasswordChange('newPassword', e.target.value)}
                          placeholder="Enter new password"
                          required
                        />
                        <Form.Text className="prf-form-hint">
                          Must be at least 8 characters long
                        </Form.Text>
                      </Form.Group>
                    </Col>
                    <Col md={6}>
                      <Form.Group className="prf-form-group">
                        <Form.Label className="prf-form-label">
                          <FaKey className="prf-label-icon" />
                          Confirm Password *
                        </Form.Label>
                        <Form.Control
                          type="password"
                          className="prf-form-input"
                          value={passwordData.confirmPassword}
                          onChange={(e) => handlePasswordChange('confirmPassword', e.target.value)}
                          placeholder="Confirm new password"
                          required
                        />
                      </Form.Group>
                    </Col>
                    <Col md={12}>
                      <Button type="submit" className="prf-btn prf-btn-secondary" disabled={AP_changingPassword}>
                        <FaKey className="prf-btn-icon" />
                        {AP_changingPassword ? 'Updating...' : 'Update Password'}
                      </Button>
                    </Col>
                  </Row>
                </Form>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </Container>
    </div>
    </AdminLayout>
  );
};

export default AdminProfile;